import { useCallback, useEffect, useState } from "react";
import { subscribeEbaySyncProgress } from "../../services/batchSocket";
import {
  getApiBaseUrl,
  getEbayBatchSyncStatus,
} from "../../services/ebaySync";
import EbaySyncProgressBar from "./EbaySyncProgressBar";
import EbaySyncDetailModal from "./EbaySyncDetailModal";
import "./ebay-sync.css";

function EbayBatchSyncBar({ batchId }) {
  const apiBaseUrl = getApiBaseUrl();
  const [statusData, setStatusData] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!batchId) return null;
    try {
      const data = await getEbayBatchSyncStatus(batchId, apiBaseUrl);
      setStatusData(data);
      setError("");
      return data;
    } catch (err) {
      setError(err?.message || "Failed to load sync status");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [batchId, apiBaseUrl]);

  useEffect(() => {
    if (!batchId) return undefined;
    setIsLoading(true);
    fetchStatus();
  }, [batchId, fetchStatus]);

  useEffect(() => {
    if (!batchId) return undefined;

    const unsubscribe = subscribeEbaySyncProgress(
      {
        batchId,
        handlers: {
          onProgress: (payload) => {
            setStatusData((prev) => ({
              ...(prev || {}),
              batch_id: payload.batch_id ?? batchId,
              is_running: payload.completed ? false : payload.status === "running",
              sync_run_id: payload.sync_run_id ?? prev?.sync_run_id,
              total: payload.total ?? prev?.total ?? 0,
              in_queue: payload.counts?.in_queue ?? prev?.in_queue ?? 0,
              listing: payload.counts?.listing ?? prev?.listing ?? 0,
              listed: payload.counts?.listed ?? prev?.listed ?? 0,
              failed: payload.counts?.failed ?? prev?.failed ?? 0,
              done: payload.counts?.done ?? prev?.done ?? 0,
              summary: payload.summary ?? prev?.summary,
            }));
          },
          onComplete: () => {
            fetchStatus();
          },
        },
      },
      apiBaseUrl
    );

    return unsubscribe;
  }, [batchId, apiBaseUrl, fetchStatus]);

  useEffect(() => {
    if (!batchId || !statusData?.is_running) return undefined;

    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, [batchId, statusData?.is_running, fetchStatus]);

  if (!batchId || isLoading) return null;

  const isRunning = statusData?.is_running;
  const hasHistory =
    (statusData?.total || 0) > 0 || statusData?.sync_run_id != null;

  if (!isRunning && !hasHistory && !error) return null;

  const counts = {
    total: statusData?.total || 0,
    in_queue: statusData?.in_queue || 0,
    listing: statusData?.listing || 0,
    listed: statusData?.listed || 0,
    failed: statusData?.failed || 0,
    done: statusData?.done || 0,
  };

  const openTask = () => {
    if (statusData?.sync_run_id) {
      setIsDetailModalOpen(true);
    }
  };

  const inProgress = counts.in_queue + counts.listing;

  return (
    <>
    <div
      className={`ebay-batch-sync-bar ${isRunning ? "" : "ebay-batch-sync-bar--idle"} ${
        isExpanded ? "ebay-batch-sync-bar--expanded" : "ebay-batch-sync-bar--collapsed"
      }`}
    >
      <div className="ebay-batch-sync-bar__header">
        <button
          type="button"
          className="ebay-batch-sync-bar__toggle"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? "Collapse eBay sync details" : "Expand eBay sync details"}
        >
          <h3 className="ebay-batch-sync-bar__title">
            {isRunning ? (
              <>
                <span className="ebay-batch-sync-bar__spinner" />
                eBay sync running
              </>
            ) : hasHistory ? (
              "Latest eBay sync"
            ) : (
              "eBay sync"
            )}
          </h3>
        </button>

        {hasHistory && !isExpanded && (
          <div className="ebay-batch-sync-bar__summary">
            <span className="ebay-batch-sync-bar__stat ebay-batch-sync-bar__stat--listed">
              <span className="ebay-batch-sync-bar__dot" />
              Listed - {counts.listed}
            </span>
            <span className="ebay-batch-sync-bar__stat ebay-batch-sync-bar__stat--progress">
              <span className="ebay-batch-sync-bar__dot" />
              In progress - {inProgress}
            </span>
            <span className="ebay-batch-sync-bar__stat ebay-batch-sync-bar__stat--failed">
              <span className="ebay-batch-sync-bar__dot" />
              Failed - {counts.failed}
            </span>
          </div>
        )}

        <div className="ebay-batch-sync-bar__actions">
          {statusData?.sync_run_id && (
            <button
              type="button"
              className="ebay-batch-sync-bar__link-btn"
              onClick={openTask}
            >
              View details
            </button>
          )}
        </div>
      </div>

      {error && <div className="ebay-sync__error">{error}</div>}

      {isExpanded && hasHistory && (
        <EbaySyncProgressBar
          counts={counts}
          status={isRunning ? "running" : "completed"}
        />
      )}
    </div>

    {isDetailModalOpen && statusData?.sync_run_id && (
      <EbaySyncDetailModal
        syncRunId={statusData.sync_run_id}
        onClose={() => setIsDetailModalOpen(false)}
      />
    )}
    </>
  );
}

export default EbayBatchSyncBar;
